<template>
  <div class="farm-area">
    <WindowModal :title="modalTitle" type="Global">
      <template #modal-content>
        <FarmAsset
          v-if="currentFarm !== null"
          ref="farmAsset"
          :farm="currentFarm"
          :action="actionPicked"
          :transaction="transaction"
          @deposit="depositTokens"
          @withdraw="withdrawTokens"
          @initConfirmState="changeModalTitle('init')"
          @initLoading="changeModalTitle('loading')"
          @initSuccess="changeModalTitle('success')"
          @initError="changeModalTitle('error')"
          @closeGlobalModal="closeModal"
        />
      </template>
    </WindowModal>
    <div class="farm-box">
      <div class="text-area">
        <h1>
          {{ $t('farm.farms') }}
        </h1>
        <p class="desc">
          {{ $t('farm.subheader1') }}
        </p>
      </div>
      <div class="image-area">
        <div class="picture">
          <picture>
            <source srcset="/img/new/floatie.webp" type="image/webp">
            <nuxt-img
              fit="inside"
              width="2767"
              sizes="sm:250px md:300px lg:400px xl:500px xxl:560px"
              src="/img/new/floatie.png"
              alt="Farm $FLOATIE and harvest coin rewards on Defi Pool Finance"
              title="Farm $FLOATIE"
            />
          </picture>
        </div>
      </div>
      <div class="cyan-cloud">
        <CloudCyanStakeLeft />
      </div>
      <div class="cyan-cloud2">
        <CloudCyanStakeRight />
      </div>
    </div>
    <div class="filter-row">
      <div class="start-area">
        <div class="switch-area">
          <Switcher v-model="filter.stakedOnly" :label="$t('stake.staked_only')" @select="changeStakedOnly" />
        </div>
        <div class="status-area">
          <button type="button" :class="{'btn-status': true, active: filter.status === 'live'}" @click.prevent="setStatus('live')">
            {{ $t('stake.live') }}
          </button>
          <button type="button" :class="{'btn-status': true, active: filter.status === 'finished'}" @click.prevent="setStatus('finished')">
            {{ $t('stake.finished') }}
          </button>
        </div>
      </div>
      <div class="end-area">
        <div class="sort-area">
          <label for="sortList">
            {{ $t('common.sort_by') }}
          </label>
          <Select v-model="filter.orderField" :options="filterOptions" />
        </div>
        <div class="search-area">
          <div class="search-input">
            <Search />
            <input v-model="filter.search" type="text" сlass="input" :placeholder="$t('common.search') + '...'">
          </div>
        </div>
      </div>
    </div>
    <div v-if="farms.length > 0 && !farmsLoading" class="farms-area">
      <div v-for="(farm, index) in farms" :key="index" class="farm-item">
        <div :ref="'farmHeader' + index" :class="{'farm-header': true, 'active': farm.opened}" @click="toggleAccordion(index)">
          <div class="logo">
            <div class="forwards">
              <ArrowBack />
            </div>
            <div class="backwards">
              <ArrowBack />
            </div>
            <nuxt-img
              class="first-token"
              :src="farm.token_from.address !== $config.ZERO ? $getImageByAddress(farm.token_from.address) : $getImage(farm.token_from.logoURI)"
              alt="$FLOATIE farm for earning auto multiple rewards"
              title="$FLOATIE farm"
              :quality="80"
              lazy
              @error="$event.target.src = require('~/static/img/default_token.svg')"
            />
            <nuxt-img
              class="second-token"
              :src="farm.token_to.address !== $config.ZERO ? $getImageByAddress(farm.token_to.address) : $getImage(farm.token_to.logoURI)"
              alt="$FLOATIE farm for earning auto multiple rewards"
              title="$FLOATIE farm"
              :quality="80"
              lazy
              @error="$event.target.src = require('~/static/img/default_token.svg')"
            />
          </div>
          <div class="header-area">
            <p class="desc">
              {{ farm.token_from.symbol + ' / ' + farm.token_to.symbol }}
            </p>
          </div>
          <div class="core">
            <div class="core-item">
              <Verified />
              <p>
                {{ $t('farm.core') }}
              </p>
            </div>
          </div>
          <div class="regular-item earned no-mobile-border">
            <div class="icon-area">
              <Coins />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('farm.earned') }}
              </p>
              <p class="value">
                {{ $priceFormat(farm.earned, 2, '.', ',') }}
              </p>
            </div>
          </div>
          <div class="regular-item apr-area">
            <div class="icon-area">
              <ChartApr />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('farm.apr') }}
              </p>
              <p v-if="farm.apr !== -1" class="value">
                {{ $priceFormat(farm.apr, 2, '.', ',') + '%' }}
              </p>
              <div v-else class="value">
                <Skeleton width="60px" height="16px" />
              </div>
            </div>
          </div>
          <div class="regular-item liquidity-area">
            <div class="icon-area">
              <Money />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('farm.liquidity') }}
              </p>
              <p v-if="farm.liquidity !== -1" class="value">
                ${{ $priceFormat(farm.liquidity, 0, '.', ',') }}
              </p>
              <div v-else class="value">
                <Skeleton width="60px" height="16px" />
              </div>
            </div>
          </div>
          <div class="regular-item multiplier-area">
            <div class="icon-area">
              <Rocket />
            </div>
            <div class="value-area">
              <p class="desc">
                {{ $t('farm.multiplier') }}
              </p>
              <p v-if="farm.multiplier !== -1" class="value">
                {{ $priceFormat(farm.multiplier, 0, '.', ',') }} X
              </p>
              <div v-else class="value">
                <Skeleton width="60px" height="16px" />
              </div>
            </div>
          </div>
          <div class="trigger-area">
            <p>
              <span class="mobile-visible">
                {{ !farm.opened ? $t('common.show') : $t('common.hide') }}
              </span>
              <ChevronDown />
            </p>
          </div>
        </div>
        <div :ref="'farmDetails' + index" :class="{'farm-details': true, 'active': farm.opened}">
          <div class="details-area">
            <div class="numbers-area">
              <div class="input-area recent-profit">
                <div class="info">
                  <div class="h6">
                    {{ farm.token_from.symbol + ' ' + $t('farm.earned') }}
                  </div>
                  <div class="h3">
                    {{ $formatCryptoAmount(farm.earned, 12) }}
                    <span class="currency">
                      {{ farm.token_from.symbol }}
                    </span>
                  </div>
                  <div class="h5">
                    {{ convertToUsd(farm.earned) }}
                    <span class="currency">
                      USD
                    </span>
                  </div>
                </div>
                <div class="action">
                  <button type="button" :class="{'btn cyan': true, 'disabled': farmLoading || farm.earned === 0}" @click="harvestTokens(farm)">
                    {{ $t('stake.harvest') }}
                  </button>
                </div>
              </div>
              <div v-if="farm.deposited === 0" class="input-area farm-actions">
                <div class="info">
                  <div class="h6">
                    {{ $t('farm.start_farming') }}
                  </div>
                  <input
                    v-model="farmAmount"
                    type="number"
                    step="0.00001"
                    lang="en"
                    min="0"
                    placeholder="0..."
                  >
                  <div class="h5">
                    {{ $formatCryptoAmount(farm.balance, 8) + ' ' + $t('farm.lp_tokens') + ' ' + $t('farm.available') }}
                  </div>
                </div>
                <div class="action">
                  <button type="button" :class="{'btn cyan': true, 'disabled': farmLoading || farmAmount === null || farmAmount === 0}" @click="depositTokens(farm)">
                    {{ $t('farm.farm') }}
                  </button>
                </div>
              </div>
              <div v-else class="input-area farm-actions">
                <div class="info">
                  <div class="h6">
                    {{ farm.token_from.symbol + '-' + farm.token_to.symbol + ' LP ' + $t('farm.staked') }}
                  </div>
                  <div class="h3">
                    {{ $formatCryptoAmount(farm.deposited, 8) }}
                    <span class="currency">
                      LPs
                    </span>
                  </div>
                  <div class="h5">
                    ~{{ convertLPToUsd(farm) }}
                    <span class="currency">
                      USD
                    </span>
                  </div>
                  <div class="h5">
                    ~{{ getFarmFirstTokenAmount(farm) + ' ' + farm.token_from.symbol }}&nbsp;&bull;&nbsp;
                    ~{{ getFarmSecondTokenAmount(farm) + ' ' + farm.token_to.symbol }}
                  </div>
                </div>
                <div class="action">
                  <button type="button" class="btn cyan" @click="toggleModal(farm, 'withdraw')">
                    {{ $t('farm.withdraw_action') }}
                  </button>
                  <button type="button" class="btn cyan" @click="toggleModal(farm, 'deposit')">
                    {{ $t('farm.deposit_action') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="help-area">
            <div class="help-list">
              <div class="item">
                <p class="bottom-link" @click="generateAddLiquidityLink(farm)">
                  <ChevronRight />
                  {{ $t('farm.get') + ' ' + farm.token_from.symbol + '-' + farm.token_to.symbol + ' LP' }}
                </p>
              </div>
              <div class="item">
                <nuxt-link class="bottom-link" :to="localePath('/docs')">
                  <ChevronRight />
                  {{ $t('stake.view_tutorial') }}
                </nuxt-link>
              </div>
              <div class="item">
                <a class="bottom-link" :href="generateScanLink(farm.lpAddress)" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('stake.view_contract') }}
                </a>
              </div>
              <div class="item">
                <nuxt-link class="bottom-link" :to="localePath(generateTokenInfoLink(farm.lpAddress))" rel="nofollow noopener noreferrer" target="_blank">
                  <ChevronRight />
                  {{ $t('farm.pair_info') }}
                </nuxt-link>
              </div>
              <div class="item">
                <p class="bottom-link" @click="$addToMetamask(farm.lp)">
                  <ChevronRight />
                  {{ $t('common.add_to_metamask') }}
                  <MetamaskIcon />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="farmsLoading" class="farms-area">
      <div v-for="index in 3" :key="index" class="farm-item">
        <div class="farm-header">
          <div class="logo">
            <Skeleton width="50px" height="50px" circle />
          </div>
          <div class="header-area">
            <Skeleton width="75px" height="16px" />
          </div>
          <div class="core">
            <Skeleton width="85px" height="32px" />
          </div>
          <div class="regular-item earned no-mobile-border">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" circle />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: 0.5rem" />
              <Skeleton width="35px" height="16px" />
            </div>
          </div>
          <div class="regular-item apr-area">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" circle />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: 0.5rem" />
              <Skeleton width="35px" height="16px" />
            </div>
          </div>
          <div class="regular-item liquidity-area">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" circle />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: 0.5rem" />
              <Skeleton width="35px" height="16px" />
            </div>
          </div>
          <div class="regular-item multiplier-area">
            <div class="icon-area">
              <Skeleton width="21px" height="21px" circle />
            </div>
            <div class="value-area">
              <Skeleton width="45px" height="14px" style="margin-bottom: 0.5rem" />
              <Skeleton width="35px" height="16px" />
            </div>
          </div>
          <div class="trigger-area">
            <Skeleton width="70px" height="16px" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Skeleton } from 'vue-loading-skeleton'

import {
  hydrateWhenIdle,
  hydrateWhenVisible
} from 'vue-lazy-hydration'

import FarmAsset from '~/components/app/interface/FarmAsset'

export default {
  name: 'FarmComponent',
  components: {
    ArrowBack: hydrateWhenIdle(() => import('~/components/svg/new/ArrowBack')),
    ChartApr: hydrateWhenIdle(() => import('~/components/svg/new/ChartApr')),
    ChevronDown: hydrateWhenIdle(() => import('~/components/svg/new/ChevronDown')),
    ChevronRight: hydrateWhenVisible(() => import('~/components/svg/new/ChevronRight')),
    CloudCyanStakeLeft: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanStakeLeft')),
    CloudCyanStakeRight: hydrateWhenIdle(() => import('~/components/svg/new/clouds/CloudCyanStakeRight')),
    Coins: hydrateWhenIdle(() => import('~/components/svg/new/Coins')),
    MetamaskIcon: hydrateWhenVisible(() => import('~/components/svg/MetamaskIcon')),
    Money: hydrateWhenIdle(() => import('~/components/svg/new/Money')),
    Rocket: hydrateWhenIdle(() => import('~/components/svg/new/Rocket')),
    Search: hydrateWhenIdle(() => import('~/components/svg/new/Search')),
    Select: hydrateWhenIdle(() => import('~/components/app/interface/Select')),
    Switcher: hydrateWhenIdle(() => import('~/components/app/interface/Switcher')),
    Verified: hydrateWhenIdle(() => import('~/components/svg/new/Verified')),
    Skeleton,
    FarmAsset,
    WindowModal: hydrateWhenIdle(() => import('~/components/modals/WindowModal'))
  },
  layout: 'new',
  data() {
    return {
      farmAmount: null,
      farmLoading: false,
      filter: {
        page: 1,
        step: 10,
        active: null,
        stable: null,
        core: null,
        boosted: null,
        stakedOnly: false,
        status: 'live',
        orderField: 'hot',
        search: ''
      },
      filterOptions: [
        {
          name: this.$t('stake.hot'),
          value: 'hot'
        },
        {
          name: this.$t('farm.multiplier'),
          value: 'multiplier'
        },
        {
          name: this.$t('stake.apr'),
          value: 'apr'
        },
        {
          name: this.$t('stake.earned'),
          value: 'earned'
        },
        {
          name: this.$t('farm.liquidity'),
          value: 'liquidity'
        },
        {
          name: this.$t('stake.latest'),
          value: 'latest'
        }
      ],
      farmsLoading: false,
      transaction: {
        gasFee: 700000,
        tx: '',
        loading: false,
        pending: false
      },
      currentFarm: null,
      actionPicked: 'deposit',
      modalTitle: this.$t('farm.you_are_depositing'),
      farms: []
    }
  },
  async fetch({ store, error, route }) {
    try {
      const pageName = route.name.split('___')
      const seoData = store.getters['seos/seosByUri'](pageName[0])
      if (seoData === null) {
        await store.dispatch('seos/fetchSeos', {
          uri: route.name
        })
      }
    } catch (err) {
      error(err)
    }
  },
  head({ $config: { appUrl } }) {
    return {
      title: this.meta ? this.meta.title : this.$t('common.launching'),
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-description',
          name: 'og:description',
          content: this.meta ? this.meta.description : ''
        },
        {
          hid: 'og-title',
          name: 'og:title',
          content: this.meta
            ? this.meta.title
            : this.$t('common.launching')
        },
        {
          hid: 'og-image',
          name: 'og:image',
          content: this.meta
            ? this.meta.image
            : appUrl + 'seo/default.jpg'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.meta ? this.meta.keyword : ''
        }
      ]
    }
  },
  computed: {
    ...mapGetters('seos', [
      'seosByUri'
    ]),
    ...mapGetters(['floatieToUsd', 'eth']),
    meta() {
      const pageName = this.$nuxt.$route.name.split('___')
      return this.seosByUri(pageName[0])
    }
  },
  mounted() {
    this.loadPoolsInfo()
  },
  methods: {
    ...mapActions('modal',
      ['changeModalGlobalState']
    ),
    ...mapActions('farms',
      ['fetchFarms']
    ),
    generateTokenInfoLink(address) {
      return '/analytics/tokens/' + address
    },
    generateScanLink(address) {
      return this.$config.chainExplorer + '/address/' + address
    },
    generateAddLiquidityLink(farm) {
      this.$router.push({
        path: '/liquidity/add',
        query: {
          from: farm.token_from.address,
          to: farm.token_to.address
        }
      })
      this.$nuxt.$emit('tokensLoaded')
    },
    changeStakedOnly(newState) {
      this.filter.stakedOnly = newState === true
    },
    setStatus(newStatus) {
      this.filter.status = newStatus
    },
    toggleAccordion(index) {
      const farmIndex = this.farms.findIndex(item => item.index === index)
      this.farms[farmIndex].opened = !this.farms[farmIndex].opened
      if (this.$refs['farmDetails' + index][0] === undefined) {
        return
      }
      if (this.farms[farmIndex].opened) {
        const height = this.$refs['farmDetails' + index][0].scrollHeight + 'px'
        this.$refs['farmDetails' + index][0].style.maxHeight = height
      } else {
        this.$refs['farmDetails' + index][0].style.maxHeight = null
      }
    },
    async loadPoolsInfo() {
      const chainId = await window.eth.eth.net.getId()
      if (!this.$isChainSupported(chainId)) {
        this.farmsLoading = false
        return
      }
      try {
        this.farmsLoading = true
        await this.fetchFarms(this.filter)
        const dbFarms = this.$store.getters['farms/farms']
        const userAddress = await this.$getUserAddress()

        const MasterChefV2File = await import('~/assets/abis/MasterChefV2.json')
        const MasterChefV2 = MasterChefV2File.default
        const masterContract = new window.eth.eth.Contract(MasterChefV2, this.$config.periodMasterv2)

        const PeriodPairFile = await import('~/assets/abis/PeriodPair.json')
        const PeriodPair = PeriodPairFile.default
        for (let ind = 0; ind < dbFarms.length; ++ind) {
          this.farms[ind] = dbFarms[ind] !== undefined && dbFarms[ind] !== null ? JSON.parse(JSON.stringify(dbFarms[ind])) : { id: 0 }
          this.farms[ind].index = ind
          this.farms[ind].opened = false
          this.farms[ind].token_from = {
            address: dbFarms[ind].t0_address,
            name: dbFarms[ind].t0_name,
            symbol: dbFarms[ind].t0_symbol,
            decimals: dbFarms[ind].t0_decimals,
            logoURI: dbFarms[ind].t0_image
          }
          this.farms[ind].token_to = {
            address: dbFarms[ind].t1_address,
            name: dbFarms[ind].t1_name,
            symbol: dbFarms[ind].t1_symbol,
            decimals: dbFarms[ind].t1_decimals,
            logoURI: dbFarms[ind].t1_image
          }
          this.farms[ind].lp = {
            address: dbFarms[ind].lpAddress,
            name: dbFarms[ind].t0_symbol + '-' + dbFarms[ind].t1_symbol + ' LP',
            symbol: 'LP',
            decimals: 18
          }
          const pairAddress = await masterContract.methods.lpToken(this.farms[ind].pid).call({ from: userAddress })
          const pairContract = new window.eth.eth.Contract(PeriodPair, pairAddress)

          const poolInfo = await masterContract.methods.poolInfo(this.farms[ind].pid).call({ from: userAddress })
          const userInfo = await masterContract.methods.userInfo(this.farms[ind].pid, userAddress).call({ from: userAddress })
          const reserves = await pairContract.methods.getReserves().call({ from: userAddress })
          const lpTotalSupply = await pairContract.methods.totalSupply().call({ from: userAddress })
          const balance = await pairContract.methods.balanceOf(userAddress).call({ from: userAddress })
          const totalAllocPoint = await masterContract.methods.totalRegularAllocPoint().call({ from: userAddress })
          const boostMultiplier = await masterContract.methods.getBoostMultiplier(userAddress, this.farms[ind].pid).call({ from: userAddress })
          const boostPrecision = await masterContract.methods.BOOST_PRECISION().call({ from: userAddress })
          const AccFloatiePrecision = await masterContract.methods.ACC_FLOATIE_PRECISION().call({ from: userAddress })

          this.farms[ind].token_from.price = this.floatieToUsd
          this.farms[ind].token_to.price = Object.prototype.hasOwnProperty.call(this.eth, 'usd_price') ? this.eth.usd_price : 1

          this.farms[ind].multiplier = parseFloat(userInfo.boostMultiplier)
          this.farms[ind].earned = parseFloat(window.eth.utils.fromWei(userInfo.amount)) *
            parseFloat(window.eth.utils.fromWei(boostMultiplier)) /
            parseFloat(window.eth.utils.fromWei(boostPrecision)) *
            parseFloat(window.eth.utils.fromWei(poolInfo.accFloatiePerShare)) /
            parseFloat(window.eth.utils.fromWei(AccFloatiePrecision)) -
            parseFloat(window.eth.utils.fromWei(userInfo.rewardDebt))
          this.farms[ind].deposited = parseFloat(window.eth.utils.fromWei(userInfo.amount))
          this.farms[ind].allocationPoint = poolInfo.allocPoint
          this.farms[ind].isRegular = poolInfo.isRegular
          this.farms[ind].accCakePerShare = poolInfo.accCakePerShare
          const poolWeight = window.eth.utils.toBN(poolInfo.allocPoint).div(window.eth.utils.toBN(totalAllocPoint))
          this.farms[ind].apr = this.$calculateFarmApr(poolWeight, this.floatieToUsd, this.farms[0].liquidity)
          this.farms[ind].balance = parseFloat(window.eth.utils.fromWei(balance))
          this.farms[ind].reserves = {
            reserve0: parseFloat(window.eth.utils.fromWei(reserves.reserve0)),
            reserve1: parseFloat(window.eth.utils.fromWei(reserves.reserve1))
          }
          this.farms[ind].liquidity = this.farms[0].reserves.reserve0 * this.farms[0].token_from.price +
        this.farms[ind].reserves.reserve1 * this.farms[0].token_to.price
          this.farms[ind].lpTotalSupply = parseFloat(window.eth.utils.fromWei(lpTotalSupply))
        }
        this.farmsLoading = false
      } catch (err) {
        console.log(err)
        this.farmsLoading = false
      }
    },
    async depositTokens(farm, modalFarmAmount = null) {
      const farmAmount = modalFarmAmount !== null ? modalFarmAmount : this.farmAmount
      const depositSummary = this.$t('farm.deposit_action') + ' ' + this.$formatCryptoAmount(farmAmount, 8) + ' ' +
        this.$t('farm.lp_tokens') + ' ' + this.$t('common.to_the_farm')
      this.$nuxt.$emit('openLoading', this.$t('farm.depositing'), depositSummary)
      this.transaction.loading = true
      this.transaction.pending = true
      this.farmLoading = true

      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        this.farmLoading = false
        return
      }
      if (farmAmount === null || parseFloat(farmAmount) === 0) {
        this.$nuxt.$emit('openError', this.$t('farm.deposit_stucked'), this.$t('stake.amount_incorrect'))
        this.farmLoading = false
        return
      }

      const userAddress = await this.$getUserAddress()

      const MasterChefV2File = await import('~/assets/abis/MasterChefV2.json')
      const MasterChefV2 = MasterChefV2File.default
      const masterContract = new window.eth.eth.Contract(MasterChefV2, this.$config.periodMasterv2)
      const PeriodPairFile = await import('~/assets/abis/PeriodPair.json')
      const PeriodPair = PeriodPairFile.default
      const pairAddress = await masterContract.methods.lpToken(farm.pid).call({ from: userAddress })
      const tokenContract = new window.eth.eth.Contract(PeriodPair, pairAddress)
      const floatFarmAmount = parseFloat(farmAmount)

      const lpTokenBalance = window.eth.utils.fromWei(await tokenContract.methods.balanceOf(userAddress).call({ from: userAddress }))
      if (parseFloat(lpTokenBalance) < floatFarmAmount) {
        this.$nuxt.$emit('openError', this.$t('farm.deposit_stucked'), this.$t('farm.low_balance'))
        this.farmLoading = false
        return
      }
      const lpTokenAllowance = window.eth.utils.toBN(await tokenContract.methods.allowance(userAddress, this.$config.periodMasterv2).call({ from: userAddress }))
      const depositAmount = window.eth.utils.toBN(
        window.eth.utils.toWei(
          Number(floatFarmAmount).toPrecision(10)
        )
      )
      if (depositAmount.gte(lpTokenAllowance)) {
        try {
          const gasPrice = await this.$calculateGasCost()
          const gasFeeEstimated = await tokenContract.methods.approve(this.$config.periodMasterv2, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
            .estimateGas({ from: userAddress, gas: 5000000 })
          await tokenContract.methods.approve(this.$config.periodMasterv2, window.eth.utils.toWei(Number(2 ** 64 - 1).toString(), 'ether'))
            .send({ from: userAddress, gas: Math.round(gasFeeEstimated * 1.15).toString(), gasPrice })
        } catch (err) {
          if (err.code === 4001) {
            this.$nuxt.$emit('openError', this.$t('farm.deposit_stucked'), this.$t('common.deined_operation'))
          } else {
            const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
            this.$nuxt.$emit('openError', this.$t('farm.deposit_stucked'), errorMessage)
          }
          this.farmLoading = false
          console.log(err)
        }
      }

      try {
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await masterContract.methods.deposit(farm.pid, depositAmount)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const deposited = await masterContract.methods.deposit(farm.pid, depositAmount)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        this.$saveTransaction({
          tid0: pairAddress,
          tid0_token: {
            address: pairAddress,
            name: this.$config.floatieName + ' LPs',
            symbol: this.$config.floatieSymbol + '-LP',
            decimals: 18
          },
          t0_amount: floatFarmAmount,
          hash: deposited.transactionHash,
          type: 'farm_deposit'
        },
        depositSummary
        )
        this.transaction.tx = deposited.transactionHash

        const farmIndex = this.getFarmIndex(farm)
        this.farms[farmIndex].balance -= floatFarmAmount
        this.farms[farmIndex].deposited += floatFarmAmount

        this.$nuxt.$emit('openSuccess', this.$t('stake.success'), this.$t('farm.success_deposit_text'), this.$config.chainExplorer + '/tx/' + this.transaction.tx)
        this.farmLoading = false
      } catch (err) {
        if (err.code === 4001) {
          this.$nuxt.$emit('openError', this.$t('farm.deposit_stucked'), this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.$nuxt.$emit('openError', this.$t('farm.deposit_stucked'), errorMessage)
          this.farmLoading = false
        }
        console.log(err)
      }
      this.transaction.loading = false
      this.transaction.pending = false
      this.farmLoading = false
      this.farmAmount = null
    },
    async withdrawTokens(farm, modalFarmAmount = null) {
      const farmAmount = modalFarmAmount !== null ? modalFarmAmount : farm.deposited
      const depositSummary = this.$t('farm.withdraw_action') + ' ' + this.$formatCryptoAmount(farmAmount, 8) + ' ' +
        this.$t('farm.lp_tokens') + ' ' + this.$t('common.from_the_farm')
      this.$nuxt.$emit('openLoading', this.$t('farm.withdrawing'), depositSummary)
      this.transaction.loading = true
      this.transaction.pending = true
      this.farmLoading = true

      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        this.farmLoading = false
        return
      }
      if (farmAmount === null || parseFloat(farmAmount) === 0) {
        this.$nuxt.$emit('openError', this.$t('farm.withdraw_stucked'), this.$t('stake.amount_incorrect'))
        this.farmLoading = false
        this.transaction.loading = false
        this.transaction.pending = false
        return
      }

      const userAddress = await this.$getUserAddress()

      const MasterChefV2File = await import('~/assets/abis/MasterChefV2.json')
      const MasterChefV2 = MasterChefV2File.default
      const masterContract = new window.eth.eth.Contract(MasterChefV2, this.$config.periodMasterv2)
      const pairAddress = await masterContract.methods.lpToken(farm.pid).call({ from: userAddress })

      const withdrawAmount = window.eth.utils.toBN(
        window.eth.utils.toWei(
          Number(parseFloat(farmAmount)).toPrecision(10)
        )
      )

      try {
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await masterContract.methods.withdraw(farm.pid, withdrawAmount)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const deposited = await masterContract.methods.withdraw(farm.pid, withdrawAmount)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        this.$saveTransaction({
          tid0: pairAddress,
          tid0_token: {
            address: pairAddress,
            name: this.$config.floatieName + ' LPs',
            symbol: this.$config.floatieSymbol + '-LP',
            decimals: 18
          },
          t0_amount: farmAmount,
          hash: deposited.transactionHash,
          type: 'farm_withdraw'
        },
        depositSummary
        )
        this.transaction.tx = deposited.transactionHash

        const farmIndex = this.getFarmIndex(farm)
        this.farms[farmIndex].balance += parseFloat(farmAmount)
        this.farms[farmIndex].deposited -= parseFloat(farmAmount)

        this.$nuxt.$emit('openSuccess', this.$t('farm.success_withdraw'), this.$t('farm.success_withdraw_text'), this.$config.chainExplorer + '/tx/' + this.transaction.tx)
        this.farmLoading = false
        this.transaction.loading = false
        this.transaction.pending = false
      } catch (err) {
        if (err.code === 4001) {
          this.$nuxt.$emit('openError', this.$t('farm.withdraw_stucked'), this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.$nuxt.$emit('openError', this.$t('farm.withdraw_stucked'), errorMessage)
          this.farmLoading = false
        }
        console.log(err)
      }
    },
    async harvestTokens(farm) {
      const depositSummary = this.$t('farm.harvest_action') + ' ' + this.$formatCryptoAmount(farm.earned, 8) + ' ' +
        this.$t('farm.lp_tokens') + ' ' + this.$t('common.from_the_farm')
      this.$nuxt.$emit('openLoading', this.$t('farm.harvesting'), depositSummary)
      this.transaction.loading = true
      this.transaction.pending = true
      this.farmLoading = true

      const chainOk = await this.$replaceChain()
      if (!chainOk) {
        this.transaction.loading = false
        this.transaction.pending = false
        this.farmLoading = false
        return
      }
      if (farm.earned === null || parseFloat(farm.earned) === 0 || parseFloat(farm.earned) < 1e-12) {
        this.$nuxt.$emit('openError', this.$t('farm.harvest_stucked'), this.$t('farm.harvest_amount_low'))
        this.farmLoading = false
        return
      }

      const userAddress = await this.$getUserAddress()

      const MasterChefV2File = await import('~/assets/abis/MasterChefV2.json')
      const MasterChefV2 = MasterChefV2File.default
      const masterContract = new window.eth.eth.Contract(MasterChefV2, this.$config.periodMasterv2)
      const pairAddress = await masterContract.methods.lpToken(farm.pid).call({ from: userAddress })
      try {
        const gasPrice = await this.$calculateGasCost()
        const depositGasFee = await masterContract.methods.deposit(farm.pid, 0)
          .estimateGas({ from: userAddress, gas: 5000000 })
        const deposited = await masterContract.methods.deposit(farm.pid, 0)
          .send({ from: userAddress, gas: Math.round(depositGasFee * 1.15).toString(), gasPrice })
        this.$saveTransaction({
          tid0: pairAddress,
          tid0_token: {
            address: pairAddress,
            name: this.$config.floatieName + ' LPs',
            symbol: this.$config.floatieSymbol + '-LP',
            decimals: 18
          },
          t0_amount: farm.earned,
          hash: deposited.transactionHash,
          type: 'farm_harvest'
        },
        depositSummary
        )
        this.transaction.tx = deposited.transactionHash

        const farmIndex = this.getFarmIndex(farm)
        this.farms[farmIndex].earned = 0

        this.$nuxt.$emit('openSuccess', this.$t('farm.harvested'), this.$t('farm.success_harvest_text'), this.$config.chainExplorer + '/tx/' + this.transaction.tx)
        this.farmLoading = false
      } catch (err) {
        if (err.code === 4001) {
          this.$nuxt.$emit('openError', this.$t('farm.harvest_stucked'), this.$t('common.deined_operation'))
        } else {
          const errorMessage = err.message !== undefined && err.message.length > 0 ? err.message : ''
          this.$nuxt.$emit('openError', this.$t('farm.harvest_stucked'), errorMessage)
          this.farmLoading = false
        }
        console.log(err)
      }
      this.transaction.loading = false
      this.transaction.pending = false
      this.farmLoading = false
    },
    convertToUsd(amount) {
      return this.$priceFormat(parseFloat(amount * this.floatieToUsd), 2)
    },
    getFarmFirstTokenAmount(farm) {
      return typeof window !== 'undefined' ? this.$formatCryptoAmount(farm.deposited / farm.lpTotalSupply * farm.reserves.reserve0, 4) : 0
    },
    getFarmSecondTokenAmount(farm) {
      return typeof window !== 'undefined' ? this.$formatCryptoAmount(farm.deposited / farm.lpTotalSupply * farm.reserves.reserve1, 4) : 0
    },
    convertLPToUsd(farm) {
      const firstTokenAmount = parseFloat(this.getFarmFirstTokenAmount(farm))
      const secondTokenAmount = parseFloat(this.getFarmSecondTokenAmount(farm))
      return this.$priceFormat(firstTokenAmount * farm.token_from.price + secondTokenAmount * farm.token_to.price, 2)
    },
    changeModalTitle(titleType) {
      switch (titleType) {
        case 'loading':
          this.modalTitle = this.$t('swap.waiting_for_confirmation')
          break
        case 'success':
          this.modalTitle = this.$t('stake.success')
          break
        case 'error':
          this.modalTitle = this.$t('swap.error_header')
          break
        default:
          this.modalTitle = this.actionPicked === 'deposit' ? this.$t('farm.you_are_depositing') : this.$t('farm.you_are_withdrawing')
      }
    },
    toggleModal(farm, action) {
      this.currentFarm = farm
      this.actionPicked = action
      this.changeModalTitle(action)
      this.changeModalGlobalState({
        state: true
      })
    },
    closeModal() {
      this.changeModalGlobalState({
        state: false
      })
    },
    showError(type, message = '') {
      this.transaction.loading = false
      this.transaction.pending = false
      this.changeModalGlobalState({
        state: true
      })
      this.$refs.farmAsset.rejectTransaction({
        type,
        message
      })
    },
    getFarmIndex(farm) {
      return this.farms.findIndex(item => item.pid === farm.pid)
    }
  }
}
</script>

<style scoped>
@import url('~/assets/css/new/farm.css');
@import url('~/assets/css/new/filter.css');
</style>
